import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ChevronRight, Play, Building2, Briefcase, Globe2, Activity, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import tourbiLogo from "@/assets/tourbi-logo.png";
import tourbiIcon from "@/assets/tourbi-icon.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const products = [
  "Travel CRM",
  "Booking Management",
  "Inventory System",
  "AI Itinerary Builder",
  "HR & People Management",
  "Account Profit & Loss",
  "AI BI Dashboard Report",
  "Marketing",
  "Operations",
  "Custom Branding",
  "Payment Hub",
  "Channel Manager",
];

const solutionsData = [
  {
    icon: Building2,
    title: "Tour Operators",
    description: "Manage complex multi-day tours, group bookings, and supplier relationships with ease.",
    features: ["Package creation", "Group management", "Supplier contracts", "Guide"],
  },
  {
    icon: Briefcase,
    title: "Travel Agencies",
    description: "Streamline retail and wholesale operations with powerful CRM and booking tools.",
    features: ["Lead management", "Multi-channel sales", "Commission tracking"],
  },
  {
    icon: Globe2,
    title: "DMCs",
    description: "Handle ground operations, local suppliers, and B2B partnerships efficiently.",
    features: ["Supplier network", "Local inventory", "Partner portal"],
  },
  {
    icon: Activity,
    title: "Activity Providers",
    description: "Sell experiences directly and through distribution channels seamlessly.",
    features: ["Real-time availability", "OTA distribution", "Mobile check-in"],
  },
  {
    icon: Users,
    title: "Corporate Travel",
    description: "Manage business travel programs with policy compliance and cost control.",
    features: ["Travel policies", "Expense management", "Reporting"],
  },
];

const navItems = [
  { label: "Products", href: "#products", hasDropdown: true, dropdownType: "products" },
  { label: "Solutions", href: "#solutions", hasDropdown: true, dropdownType: "solutions" },
  { label: "Features", href: "#features" },
  { label: "About Us", href: "/about", isRoute: true },
  { label: "FAQ's", href: "/product-faqs", isRoute: true },
  { label: "Pricing", href: "/pricing", isRoute: true },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showFloatingDemo, setShowFloatingDemo] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState<typeof solutionsData[0] | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Show floating demo button on scroll (mobile only)
  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingDemo(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.isRoute) {
      navigate(item.href);
    } else if (item.href.startsWith("#")) {
      const element = document.querySelector(item.href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMenuOpen(false);
  };

  const handleDemoClick = () => {
    navigate("/demo");
    setIsMenuOpen(false);
  };

  // Don't show floating button on demo page
  const isOnDemoPage = location.pathname === "/demo";

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="section-container">
          <div className="flex items-center justify-between h-14 md:h-16 lg:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2" aria-label="Tourbi - Go to homepage">
              <img src={tourbiIcon} alt="Tourbi logo" width={40} height={40} className="h-8 md:h-9 lg:h-10 w-auto" />
            </a>

            {/* Tablet Navigation - Compact */}
            <nav className="hidden md:flex lg:hidden items-center gap-0.5">
              {navItems.slice(0, 3).map((item) => (
                item.hasDropdown ? (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger className="flex items-center gap-0.5 px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors outline-none">
                      {item.label}
                      <ChevronDown className="h-3 w-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64 bg-background border border-border shadow-lg z-50">
                      {item.dropdownType === "products" ? (
                        products.map((dropdownItem) => (
                          <DropdownMenuItem
                            key={dropdownItem}
                            className="cursor-default text-foreground text-sm"
                          >
                            {dropdownItem}
                          </DropdownMenuItem>
                        ))
                      ) : (
                        solutionsData.map((solution) => (
                          <DropdownMenuItem
                            key={solution.title}
                            className="cursor-pointer text-foreground text-sm hover:bg-primary/10 flex items-center gap-2"
                            onClick={() => setSelectedSolution(solution)}
                          >
                            <solution.icon className="h-4 w-4 text-primary" />
                            {solution.title}
                          </DropdownMenuItem>
                        ))
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item)}
                    className="px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </button>
                )
              ))}
              <button
                onClick={handleDemoClick}
                className="px-2 py-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
              >
                <Play className="h-3 w-3" />
                Demo
              </button>
            </nav>

            {/* Desktop Navigation - Full */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                item.hasDropdown ? (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors outline-none">
                      {item.label}
                      <ChevronDown className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-72 bg-background border border-border shadow-lg z-50">
                      {item.dropdownType === "products" ? (
                        products.map((dropdownItem) => (
                          <DropdownMenuItem
                            key={dropdownItem}
                            className="cursor-default text-foreground"
                          >
                            {dropdownItem}
                          </DropdownMenuItem>
                        ))
                      ) : (
                        solutionsData.map((solution) => (
                          <DropdownMenuItem
                            key={solution.title}
                            className="cursor-pointer text-foreground hover:bg-primary/10 flex items-center gap-3 py-2"
                            onClick={() => setSelectedSolution(solution)}
                          >
                            <div className="p-1.5 rounded-lg bg-primary/10">
                              <solution.icon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium">{solution.title}</div>
                              <div className="text-xs text-muted-foreground line-clamp-1">{solution.description}</div>
                            </div>
                          </DropdownMenuItem>
                        ))
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item)}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </button>
                )
              ))}
              {/* Desktop Request a Demo - styled prominently */}
              <button
                onClick={handleDemoClick}
                className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
              >
                <Play className="h-3.5 w-3.5" />
                Request a Demo
              </button>
            </nav>

            {/* Tablet CTA Buttons - Compact */}
            <div className="hidden md:flex lg:hidden items-center gap-2">
              <Button variant="ghost" size="sm" className="text-xs px-2" onClick={() => navigate("/login")}>
                Log in
              </Button>
              <Button variant="hero" size="sm" className="text-xs px-3" onClick={() => navigate("/trial")}>
                Start Free
              </Button>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")}>
                Log in
              </Button>
              <Button variant="hero" size="sm" onClick={() => navigate("/trial")}>
                Get Started Free
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-background border-b border-border overflow-hidden"
            >
              <nav className="section-container py-4 flex flex-col gap-1">
                {/* Request a Demo - Prominent at top */}
                <motion.button
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 }}
                  onClick={handleDemoClick}
                  className="flex items-center justify-between px-4 py-3 bg-primary/10 text-primary font-semibold rounded-xl mb-2 hover:bg-primary/20 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    Request a Demo
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </motion.button>

                {/* Nav Items with collapsible dropdowns */}
                {navItems.map((item, index) => (
                  item.hasDropdown ? (
                    <Collapsible
                      key={item.label}
                      open={openDropdown === item.label}
                      onOpenChange={(open) => setOpenDropdown(open ? item.label : null)}
                    >
                      <CollapsibleTrigger asChild>
                        <motion.button
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * (index + 1) }}
                          className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors"
                        >
                          {item.label}
                          <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                        </motion.button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-4 pl-4 border-l border-border/50 py-2 space-y-1">
                          {item.dropdownType === "products" ? (
                            products.map((dropdownItem) => (
                              <div
                                key={dropdownItem}
                                className="px-3 py-2 text-sm text-muted-foreground"
                              >
                                {dropdownItem}
                              </div>
                            ))
                          ) : (
                            solutionsData.map((solution) => (
                              <button
                                key={solution.title}
                                onClick={() => setSelectedSolution(solution)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-primary/10 rounded-lg transition-colors text-left"
                              >
                                <solution.icon className="h-4 w-4 text-primary flex-shrink-0" />
                                <span>{solution.title}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <motion.button
                      key={item.label}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * (index + 1) }}
                      onClick={() => handleNavClick(item)}
                      className="px-4 py-3 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors text-left"
                    >
                      {item.label}
                    </motion.button>
                  )
                ))}

                {/* CTA Buttons */}
                <div className="flex flex-col gap-2 pt-4 border-t border-border mt-3">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => { navigate("/login"); setIsMenuOpen(false); }}
                  >
                    Log in
                  </Button>
                  <Button 
                    variant="hero" 
                    className="w-full" 
                    onClick={() => { navigate("/trial"); setIsMenuOpen(false); }}
                  >
                    Get Started Free
                  </Button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Floating Demo Button - Mobile only */}
      <AnimatePresence>
        {showFloatingDemo && !isOnDemoPage && (
          <motion.button
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            onClick={handleDemoClick}
            className="md:hidden fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground font-semibold rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95"
          >
            <Play className="h-4 w-4" />
            Request Demo
          </motion.button>
        )}
      </AnimatePresence>

      {/* Solution Detail Dialog */}
      <Dialog open={!!selectedSolution} onOpenChange={(open) => !open && setSelectedSolution(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              {selectedSolution && (
                <div className="p-2 rounded-xl bg-primary/10">
                  <selectedSolution.icon className="h-6 w-6 text-primary" />
                </div>
              )}
              <DialogTitle className="text-xl font-display">{selectedSolution?.title}</DialogTitle>
            </div>
            <DialogDescription className="text-base text-foreground/80">
              {selectedSolution?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">Key Features</h4>
            <div className="flex flex-wrap gap-2">
              {selectedSolution?.features.map((feature) => (
                <span
                  key={feature}
                  className="px-3 py-1.5 text-sm font-medium rounded-full bg-primary/10 text-primary"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <Button 
              variant="hero" 
              className="flex-1"
              onClick={() => {
                setSelectedSolution(null);
                navigate("/demo");
              }}
            >
              Request a Demo
            </Button>
            <Button 
              variant="outline"
              onClick={() => setSelectedSolution(null)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};